<template>
  <div ref="eventBlockEl" class="event-block">
    <Timeline
      class="progress-bar"
      :event="eventOrPause"
      :hide-time="!!draggingEvent"
    />
    <div class="dragging-block">
      <component
        :is="droppable"
        :accepts-type="['allocation', 'task']"
        :accepts-data="acceptsData"
        @dragover="(e: DraggedAllocation | DraggedTask) => onDragOver(e)"
        @dragleave="() => onDragLeave()"
        @drop="(e: DraggedAllocation | DraggedTask) => onDrop(e)"
      >
        <component
          :is="draggable"
          :key="event.id"
          :data="task ? { task: task, event: event } : { event: event }"
          :type="task ? 'allocation' : 'event'"
          :style="[draggingStyle]"
          @dragend="onDragEnd"
        >
          <ResizableEvent
            v-if="event.editable"
            class="block"
            :class="{ dragOver }"
            :base="base"
            :dragging-over="draggingOver"
            :dragged-event="draggingEvent"
            :event="adjustedEvent"
            :given-task="task"
            :is-recording="isRecording"
            :max-start-date="maxStartDate"
            :max-end-date="maxEndDate"
            :is-selected="selectedEventId === event.id"
            @force-rerender="$emit('forceRerender')"
            @select="(id) => $emit('select', id)"
            @unselect="$emit('unselect')"
          />
          <EventElement
            v-else
            :base="base"
            :dragging-over="draggingOver"
            :dragged-event="draggingEvent"
            :event="adjustedEvent"
            :given-task="task"
            :is-recording="isRecording"
            class="block"
            :is-selected="selectedEventId === event.id"
            @select="(id) => $emit('select', id)"
            @unselect="$emit('unselect')"
          />
        </component>
      </component>
    </div>
  </div>
</template>

<script lang="ts">
import { addDays, isSameDay } from "date-fns";
import { isEqual } from "lodash-es";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";
import { Drag, Drop } from "vue-easy-dnd";

import type { EventOrPause, FullEvent } from "@ctrl/controller-rpc";

import {
  DndEvent,
  useDndEvent,
  useEventDragging,
} from "@/components/agenda/dnd";
import EventElement from "@/components/agenda/Event.vue";
import { useHeightOfDuration } from "@/components/agenda/pixels";
import ResizableEvent from "@/components/agenda/ResizableEvent.vue";
import Timeline from "@/components/agenda/Timeline.vue";
import { getDaytime } from "@/components/dates";
import * as inject from "@/components/inject";
import { safeUpdateEvent } from "@/components/modal";
import { isEllipsisActive } from "@/components/utils";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { DraggedAllocation, DraggedTask } from "@/types";
import { isBoundTaskId } from "@/utils/id";

import { isCurrentEvent } from "./agenda";
import { Boundaries } from "./Events.vue";

const base = 40;

export default defineComponent({
  components: {
    ResizableEvent,
    EventElement,
    Drag,
    Drop,
    Timeline,
  },
  props: {
    boundaries: {
      type: Object as PropType<Boundaries>,
      required: true,
    },
    date: { type: Date, required: true },
    draggingOver: { type: Boolean, required: true },
    event: { type: Object as PropType<FullEvent>, required: true },
    eventAfter: { type: Object as PropType<EventOrPause>, required: false },
    eventBefore: { type: Object as PropType<EventOrPause>, required: false },
    isRecording: { type: Boolean, required: false, default: false },
    selectedEventId: { type: String, required: false },
    eventsEl: {
      type: Object as PropType<HTMLElement | undefined>,
      required: false,
    },
  },
  emits: {
    forceRerender: () => true,
    select: (_id: string) => true,
    setCurrentEventEl: (_: HTMLElement) => true,
    unselect: () => true,
    update: () => true,
  },
  async setup(props, { emit }) {
    const now = inject.clock();
    const controller = inject.controller();
    const locale = inject.locale();
    const multipleEventsModal = inject.multipleEventsModal();
    const simpleModal = inject.simpleModal();
    const { boundaries, date, event, eventBefore, eventAfter, eventsEl } =
      toRefs(props);
    const eventBlockEl = ref<HTMLElement>();
    const dragOver = ref(false);

    // Compute adjusted start/end times for multi-day events
    const isMultiDayEvent = computed(() => {
      return !isSameDay(event.value.start, event.value.end);
    });
    const isMultiDayEventsFirstDay = computed(() => {
      return isSameDay(date.value, event.value.start);
    });
    const isMultiDayEventsLastDay = computed(() => {
      return isSameDay(date.value, event.value.end);
    });
    // Adjust start time for multi-day events: use actual start if first day, otherwise midnight
    const adjustedStart = computed<Date>(() => {
      if (!isMultiDayEvent.value || isMultiDayEventsFirstDay.value) {
        return event.value.start;
      }
      const startOfDay = new Date(date.value);
      startOfDay.setHours(0, 0, 0, 0);
      return startOfDay;
    });
    // Adjusted end time: use midnight for the first day
    const adjustedEnd = computed<Date>(() => {
      if (!isMultiDayEvent.value || isMultiDayEventsLastDay.value) {
        return event.value.end;
      }
      const endOfDay = addDays(date.value, 1);
      endOfDay.setHours(0, 0, 0, 0);
      return endOfDay;
    });

    onMounted(() => {
      if (
        isCurrentEvent(adjustedStart.value, adjustedEnd.value, now) &&
        eventBlockEl.value
      )
        emit("setCurrentEventEl", eventBlockEl.value);
    });
    onBeforeUnmount(() => (eventBlockEl.value = undefined));

    const boundaryStartDate = computed<Date>(() => {
      const boundaryStartDate = new Date(adjustedStart.value);
      boundaryStartDate.setHours(
        boundaries.value.start.hours,
        boundaries.value.start.minutes || 0
      );
      return boundaryStartDate;
    });
    const boundaryEndDate = computed<Date>(() => {
      const boundaryEndDate = new Date(adjustedEnd.value);
      boundaryEndDate.setHours(
        boundaries.value.end.hours,
        boundaries.value.end.minutes || 0
      );
      return boundaryEndDate;
    });
    const maxStartDate = computed<Date>((): Date => {
      if (!eventBefore.value) {
        return boundaryStartDate.value;
      }
      // There is an event before.
      if (eventBefore.value.type !== "pause") {
        return eventBefore.value.end;
      }
      // There is a pause (i.e and then another event even earlier)
      else if (
        eventBefore.value.type === "pause" &&
        eventBefore.value.kind === "pause"
      )
        return eventBefore.value.start;
      // There is nothing before, known as "morning"
      else return boundaryStartDate.value;
    });
    const maxEndDate = computed<Date>((): Date => {
      if (!eventAfter.value) {
        return boundaryEndDate.value;
      }
      // There is an event after.
      if (eventAfter.value.type !== "pause") {
        return eventAfter.value.start;
      }
      // There is a pause (i.e and then another event even later)
      else if (
        eventAfter.value.type === "pause" &&
        eventAfter.value.kind === "pause"
      )
        return eventAfter.value.end;
      // There is nothing after, known as "evening"
      else return boundaryEndDate.value;
    });

    // Block State
    // Create a computed event with adjusted times for multi-day events
    const adjustedEvent = computed(() => ({
      ...event.value,
      start: adjustedStart.value,
      end: adjustedEnd.value,
    }));
    const { ongoing } = useHeightOfDuration(adjustedEvent, now, base);
    const releaser = useReleaser();
    const task = await releaser.add(
      bindAsync(event, async (event) => {
        const task_id = event.task;
        return task_id
          ? await controller.task.get(task_id)
          : releasableRef(null);
      })
    );

    // Drag n Drop
    const draggable = computed(() => {
      return !!task.value || event.value.editable ? Drag : "div";
    });
    const droppable = computed(() => {
      return !!task.value || event.value.attendable ? Drop : "div";
    });
    const max = computed(() => {
      return { start: maxStartDate.value, end: maxEndDate.value };
    });
    const {
      draggingStyle,
      draggingEvent,
      release: releaseEventDragging,
    } = useEventDragging(event.value.id, max, eventsEl);
    releaser.defer(releaseEventDragging);
    const dndEvent = computed(useDndEvent);

    const onDragEnd = (data: DndEvent) => {
      /* This is to only fire an update event when we drag the event in the right part of the app.
       * We're looking if there is a class .events in any parent of the target where we drop the event. */
      const target = data.native.target;
      if (
        target instanceof HTMLElement &&
        target.closest(".events") &&
        data.success &&
        draggingEvent.value
      ) {
        const patch = {
          id: draggingEvent.value.id,
          time: {
            start_time: draggingEvent.value.start,
            end_time: draggingEvent.value.end,
          },
        };
        if (
          !isEqual(adjustedStart.value, draggingEvent.value.start) ||
          !isEqual(adjustedEnd.value, draggingEvent.value.end)
        ) {
          safeUpdateEvent(
            "update",
            event.value.id,
            { controller, simpleModal, multipleEventsModal },
            { patch }
          );
          // Emit update event to trigger scroll position preservation
          emit("update");
        }
      }
    };
    const eventOrPause = computed<EventOrPause>(() => {
      if (draggingEvent.value) {
        return {
          ...event.value,
          ...draggingEvent.value,
          start: adjustedStart.value,
          end: adjustedEnd.value,
          type: "event",
        };
      } else
        return {
          ...event.value,
          start: adjustedStart.value,
          end: adjustedEnd.value,
          type: "event",
        };
    });

    // Refuse the drop (no drop affordance, no drop event) for tasks that
    // cannot be moved this way: tasks whose source forbids it (`canMove`),
    // and bound tasks (database rows, `object:…` ids) — even movable ones,
    // since `task.move` only accepts native task ids and fatals on anything
    // else (cf. the dispatcher in `@/utils/move`).
    const isMovableHere = (task: { id?: string; canMove?: boolean }) =>
      task.canMove !== false && !isBoundTaskId(task.id ?? "");
    const acceptsData = (data: { task?: { id?: string; canMove?: boolean } }) =>
      !data?.task || isMovableHere(data.task);

    const onDragOver = (e: DraggedAllocation | DraggedTask) => {
      if (!e) return;
      switch (e.type) {
        case "allocation": {
          if (event.value.task && e.data.task.id === event.value.task) return;
          break;
        }
        case "task": {
          if (!isMovableHere(e.data.task)) return;
          if (event.value.task && e.data.task.id === event.value.task) return;
          break;
        }
      }
      dragOver.value = true;
    };
    const onDragLeave = () => {
      dragOver.value = false;
    };
    // Clear highlight if drag cancels (e.g., ESC)
    releaser.defer(
      watch(dndEvent, (v) => {
        if (!v) dragOver.value = false;
      })
    );
    const onDrop = (e: DraggedAllocation | DraggedTask) => {
      // Reset drag over state on drop
      dragOver.value = false;
      if (e.success) {
        switch (e.type) {
          case "allocation": {
            // Guard against dropping onto the same event i.e for allocations
            if (e.data.event.id === event.value.id) return;
            if (event.value.task) {
              // Dropping an allocation onto another allocation
              if (e.data.task.id === event.value.task) return;
              controller.task.move(
                e.data.task.id,
                {
                  id: { kind: "task", id: event.value.task },
                },
                "app"
              );
            } else {
              // Dropping an allocation onto an event
              controller.task.move(
                e.data.task.id,
                {
                  id: { kind: "event", id: event.value.id },
                },
                "app"
              );
            }
            break;
          }
          case "task": {
            if (!isMovableHere(e.data.task)) return;
            if (event.value.task) {
              // We are dropping a task onto an allocation.
              if (e.data.task.id === event.value.task) return;
              controller.task.move(
                e.data.task.id,
                {
                  id: { kind: "task", id: event.value.task },
                },
                "app"
              );
            } else {
              // We are dropping a task onto an event.
              if (e.data.task.id === event.value.id) return;
              controller.task.move(
                e.data.task.id,
                {
                  id: { kind: "event", id: event.value.id },
                },
                "app"
              );
            }
            break;
          }
        }
      }
    };

    return {
      adjustedEvent,
      base,
      dragOver,
      dndEvent,
      acceptsData,
      onDragOver,
      onDragLeave,
      onDrop,
      draggable,
      droppable,
      draggingEvent,
      draggingStyle,
      eventBlockEl,
      eventOrPause,
      getDaytime,
      isEllipsisActive,
      locale,
      maxEndDate,
      maxStartDate,
      onDragEnd,
      ongoing,
      task,
    };
  },
});
</script>

<style scoped lang="scss">
.event-block {
  width: calc(100% - 24px);
  height: 100%;
  position: relative;
  z-index: $z-index-content-high;
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  .dragging-block {
    width: 100%;
    margin: 6px 0;
    min-width: 0;
    .block {
      margin-inline-start: 12px;
    }

    :deep(.block:not(.past):not(.event)) {
      box-sizing: border-box;
      border-radius: $border-radius-m;
      @include themed() {
        border: 1px solid transparent;
      }
    }

    :deep(.dragOver),
    :deep(.dnd-drop.drop-in.drop-allowed .event) {
      @include themed() {
        border: 1px dashed t($secondary-50) !important;
      }
    }
  }
}
</style>
